import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { startTransition, useCallback, useMemo, useState } from 'react';

import { dashboardUserListInvitesOptions, dashboardUserListInvitesQueryKey } from '@column/api';
import { PlatformSwitcher, StatusIndicator } from '@column/ui';

import { sandboxOptions } from '~/lib/api/sandboxOptions';
import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { UserRepository } from '~/repositories';
import { getRoleLabel } from '~/repositories/RoleRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log } from '~/util';

type PlatformItem = {
  id: string;
  name: string;
  description: string;
  isInvite?: boolean;
  isLiveEnabled?: boolean;
};

const STORAGE_KEY = 'column:platform-modes';

const loadPlatformModes = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const savePlatformMode = (platformId: string, isSandbox: boolean) => {
  try {
    const modes = loadPlatformModes();
    modes[platformId] = isSandbox;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(modes));
  } catch {
    // noop
  }
};

export const NavPlatform: React.FC = () => {
  const { currentUser, loadSession, switchPlatform, isSandbox } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [platformModes, setPlatformModes] = useState<Record<string, boolean>>(loadPlatformModes);

  const activePlatformId = currentUser?.defaultPlatformId ?? '';
  const isLive = !isSandbox;

  const updatePlatformMode = useCallback((platformId: string, sandbox: boolean) => {
    setPlatformModes((prev) => ({ ...prev, [platformId]: sandbox }));
    savePlatformMode(platformId, sandbox);
  }, []);

  const { data: invitesData } = useQuery({
    ...dashboardUserListInvitesOptions({ path: { dashboard_user_id: currentUser?.id ?? '' }, ...sandboxOptions }),
    enabled: !!currentUser?.id,
    initialData: () => (currentUser?.platformInvites ? { invites: currentUser.platformInvites } : undefined),
  });

  const platforms = useMemo<PlatformItem[]>(() => {
    if (!currentUser?.platformRoles || currentUser.platformRoles.length === 0) {
      return [];
    }
    return currentUser.platformRoles.map((entry) => ({
      id: entry.platformId,
      name: entry.platformName,
      description: getRoleLabel(entry.name),
      isLiveEnabled: entry.isLiveEnabled,
    }));
  }, [currentUser?.platformRoles]);

  const invites = useMemo<PlatformItem[]>(() => {
    if (!invitesData?.invites || invitesData.invites.length === 0) {
      return [];
    }

    return invitesData.invites.map((entry) => ({
      id: entry.platform_id ?? '',
      name: entry.platform_name ?? '',
      description: getRoleLabel(entry.platform_role),
      isInvite: true,
    }));
  }, [invitesData?.invites]);

  const allPlatforms = useMemo(() => {
    if (invites.length === 0) return platforms;
    if (platforms.length === 0) return invites;
    return [...platforms, ...invites];
  }, [platforms, invites]);

  const executeReCAPTCHA = useReCAPTCHA();

  const acceptInviteMutation = useMutation({
    mutationFn: async ({ platformId }: { platformId: string }) => {
      await UserRepository.acceptInvite({ platformId });
      await loadSession({ preferredPlatformId: platformId });
    },
    onError: (error: unknown) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Something went wrong, please try again.',
      });
    },
    onSuccess: () => {
      addSuccessNotification({ content: 'Invite accepted' });
      queryClient.invalidateQueries({
        queryKey: dashboardUserListInvitesQueryKey({ path: { dashboard_user_id: currentUser?.id ?? '' } }),
      });
    },
  });

  const handleAcceptInvite = useCallback(
    (id: string) => {
      if (!acceptInviteMutation.isPending) {
        acceptInviteMutation.mutate({ platformId: id });
      }
    },
    [acceptInviteMutation]
  );

  const handlePlatformChange = useCallback(
    (id: string) => {
      if (!currentUser || isLoading) return;

      if (!currentUser.platformRoles.some((role) => role.platformId === id)) {
        addDangerNotification({ content: 'Platform role not found' });
        return;
      }

      updatePlatformMode(activePlatformId, !!isSandbox);

      setIsLoading(true);

      executeReCAPTCHA(RecaptchaAction.UpdateUser, { optional: true })
        .then((reCaptchaToken) =>
          currentUser
            .update({ defaultPlatformId: id }, reCaptchaToken)
            .then(() => {
              setIsLoading(false);

              startTransition(() => {
                switchPlatform(id, platformModes[id] ?? false)
                  .then(() => {
                    log({ name: 'Platform changed', context: useSessionStore.getState().currentUser });
                    addSuccessNotification({ content: 'Platform changed' });
                  })
                  .catch(() => {
                    // Platform load failed but switch already completed
                  });
              });
            })
            .catch((error: Error) => {
              setIsLoading(false);
              addDangerNotification({ content: error.message });
            })
        )
        .catch((err: Error) => {
          setIsLoading(false);
          addDangerNotification({ content: err.message });
        });
    },
    [
      currentUser,
      isLoading,
      isSandbox,
      platformModes,
      activePlatformId,
      updatePlatformMode,
      switchPlatform,
      addDangerNotification,
      addSuccessNotification,
      executeReCAPTCHA,
    ]
  );

  const renderIcon = useCallback(
    (platform: PlatformItem, isActive: boolean) => {
      // Don't show status indicator for invitations
      if (platform.isInvite) {
        return null;
      }

      let mode: 'live' | 'sandbox';
      if (isActive) {
        mode = isLive ? 'live' : 'sandbox';
      } else {
        const saved = platformModes[platform.id];
        mode = saved === undefined ? 'sandbox' : saved ? 'sandbox' : 'live';
      }
      return <StatusIndicator mode={mode} className="!size-[18px] shrink-0 [&>div]:!size-2" />;
    },
    [isLive, platformModes]
  );

  if (allPlatforms.length === 0) return null;

  return (
    <PlatformSwitcher
      platforms={allPlatforms}
      value={activePlatformId}
      onValueChange={handlePlatformChange}
      onAcceptInvite={handleAcceptInvite}
      isLive={isLive}
      isLoading={isLoading}
      renderIcon={renderIcon}
      groupLabel="Platforms"
    />
  );
};
